import React, { useState, useMemo } from 'react';
import { TrendingUp } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { selectChangeHistory } from 'store/segmentChangeHistory';
import { ISegment } from 'types';
import { useSelector } from 'react-redux';
import { NODE_COLORS } from 'utils/workflowNodeHelpers';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface MembershipChartProps {
  segment?: ISegment;
}

const MembershipChart: React.FC<MembershipChartProps> = ({ segment }) => {
  const [activeTab, setActiveTab] = useState<'members' | 'change'>('members');
  const changeHistory = useSelector(selectChangeHistory);

  // Prepare chart data for Chart.js
  const chartData = useMemo(() => {
    if (changeHistory.length === 0) return { labels: [], datasets: [] };

    const labels = changeHistory.map((item) => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    });

    if (activeTab === 'members') {
      let previousValue: number | null = null;
      const processedData = changeHistory.map((item) => {
        const currentValue = item.currentContacts;
        if (currentValue === 0 && previousValue !== null) {
          return previousValue;
        }
        if (currentValue !== 0) {
          previousValue = currentValue;
        }
        return currentValue;
      });

      return {
        labels,
        datasets: [
          {
            label: 'Total membership',
            data: processedData,
            borderColor: NODE_COLORS.purple,
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            borderWidth: 3,
            fill: true,
            tension: 0.1,
            pointBackgroundColor: NODE_COLORS.purple,
            pointBorderColor: NODE_COLORS.purple,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ],
      };
    } else {
      return {
        labels,
        datasets: [
          {
            label: 'Added contacts',
            data: changeHistory.map((item) => item.addedContacts),
            borderColor: NODE_COLORS.success,
            backgroundColor: 'rgba(45, 143, 111, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.1,
            pointBackgroundColor: NODE_COLORS.success,
            pointBorderColor: NODE_COLORS.success,
            pointRadius: 3,
            pointHoverRadius: 5,
          },
          {
            label: 'Removed contacts',
            data: changeHistory.map((item) => item.removedContacts),
            borderColor: NODE_COLORS.destructive,
            backgroundColor: 'rgba(196, 77, 77, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.1,
            pointBackgroundColor: NODE_COLORS.destructive,
            pointBorderColor: NODE_COLORS.destructive,
            pointRadius: 3,
            pointHoverRadius: 5,
          },
          {
            label: 'Current contacts',
            data: changeHistory.map((item) => item.currentContacts),
            borderColor: NODE_COLORS.info,
            backgroundColor: 'rgba(74, 143, 196, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.1,
            pointBackgroundColor: NODE_COLORS.info,
            pointBorderColor: NODE_COLORS.info,
            pointRadius: 3,
            pointHoverRadius: 5,
          },
        ],
      };
    }
  }, [changeHistory, activeTab]);

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // We'll use our custom legend
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: (context: any) => {
            return context[0].label;
          },
          label: (context: any) => {
            return `${context.dataset.label}: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: true,
          color: 'rgba(229, 231, 235, 0.5)',
          drawBorder: false,
        },
        ticks: {
          color: NODE_COLORS.muted,
          font: {
            size: 12,
          },
          maxTicksLimit: 6,
        },
      },
      y: {
        display: true,
        grid: {
          display: true,
          color: 'rgba(229, 231, 235, 0.5)',
          drawBorder: false,
        },
        ticks: {
          color: NODE_COLORS.muted,
          font: {
            size: 12,
          },
          stepSize: 1,
        },
        beginAtZero: true,
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            Membership Trends
          </h3>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-bold text-foreground">
              {segment?.contacts?.length || 0}
            </span>
            <span className="text-sm text-muted-foreground">total members</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6">
        <button
          onClick={() => setActiveTab('members')}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'members'
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          Members
        </button>
        <button
          onClick={() => setActiveTab('change')}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'change'
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          Change
        </button>
      </div>

      {/* Legend */}

      {/* Chart */}
      <div className="relative h-64 bg-muted/30 rounded-lg p-4">
        {changeHistory.length > 0 ? (
          <Line data={chartData} options={chartOptions} />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-2">
                <TrendingUp className="w-6 h-6 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">No data available</p>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-center items-center gap-4 mt-4 pt-4 border-t border-border">
        {activeTab === 'members' ? (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-chart-7 rounded-sm"></div>
            <span className="text-sm text-muted-foreground">
              Total membership
            </span>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-success rounded-sm"></div>
              <span className="text-sm text-muted-foreground">
                Added contacts
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-destructive rounded-sm"></div>
              <span className="text-sm text-muted-foreground">
                Removed contacts
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-primary rounded-sm"></div>
              <span className="text-sm text-muted-foreground">
                Current contacts
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MembershipChart;
